import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Button from './Button';

const SEVERITY_CONFIG = {
  default: { icon: HelpOutlineIcon, color: 'primary' },
  info: { icon: InfoOutlinedIcon, color: 'info' },
  warning: { icon: WarningAmberIcon, color: 'warning' },
  error: { icon: ErrorOutlineIcon, color: 'error' },
};

/**
 * Confirmation or alert dialog with consistent actions and styling.
 *
 * @param {'confirm'|'alert'} variant - confirm shows Cancel + Confirm; alert shows a single OK button
 */
export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  children,
  variant = 'confirm',
  severity = 'default',
  confirmLabel,
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  confirmDisabled = false,
  loading = false,
  disableBackdropClose = false,
  maxWidth = 'xs',
}) {
  const isAlert = variant === 'alert';
  const { icon: Icon, color } = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.default;
  const resolvedConfirmLabel = confirmLabel || (isAlert ? 'OK' : 'Confirm');
  const resolvedConfirmVariant =
    confirmVariant || (severity === 'error' ? 'danger' : 'primary');

  const handleClose = (_event, reason) => {
    if (disableBackdropClose && reason === 'backdropClick') return;
    onClose?.();
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    } else {
      onClose?.();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: { borderRadius: 'var(--radius-lg)' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 1,
          fontWeight: 600,
          fontSize: 'var(--font-size-md)',
        }}
      >
        <Icon color={color} fontSize="small" />
        {title || (isAlert ? 'Notice' : 'Confirm action')}
      </DialogTitle>
      <DialogContent>
        {children || (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: 'pre-line' }}
          >
            {message}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        {!isAlert && (
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            {cancelLabel}
          </Button>
        )}
        <Button
          variant={resolvedConfirmVariant}
          onClick={handleConfirm}
          loading={loading}
          disabled={confirmDisabled}
          autoFocus={!children}
        >
          {resolvedConfirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
